import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Group,
  Modal,
  Radio,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useModalContainer } from '../../../contexts/ModalContainerContext';
import { busyActionButtonProps } from './taskModalButtonStyles';
import { TaskModalBusyOverlay } from './TaskModalBusyOverlay';

export type SetProjectBaselineStep = 'save-gate' | 'confirm';

export type SetBaselineCreateMode = 'approve' | 'draft';

export interface SetBaselineConfirmOptions {
  mode: SetBaselineCreateMode;
  baselineName: string;
  /** Omit / undefined = next free slot on the server. */
  baselineIndex?: number;
}

export interface SetProjectBaselineModalProps {
  opened: boolean;
  step: SetProjectBaselineStep;
  projectDisplayName?: string | null;
  /** True while save or create-baseline request is in flight (parent-controlled). */
  submitting: boolean;
  /** When set, create is blocked because an open revision already exists. */
  activeRevisionBlockedMessage?: string | null;
  onClose: () => void;
  /** Save unsaved changes then continue to confirm (save-gate step). */
  onSaveAndContinue: () => void | Promise<void>;
  /** Create baseline / draft from the schedule already saved on the server. */
  onConfirmCreate: (options: SetBaselineConfirmOptions) => void | Promise<void>;
}

export function SetProjectBaselineModal({
  opened,
  step,
  projectDisplayName,
  submitting,
  activeRevisionBlockedMessage = null,
  onClose,
  onSaveAndContinue,
  onConfirmCreate,
}: SetProjectBaselineModalProps) {
  const modalContainer = useModalContainer();
  const [actionPending, setActionPending] = useState(false);
  const [mode, setMode] = useState<SetBaselineCreateMode>('approve');
  const [baselineName, setBaselineName] = useState('');
  const busy = submitting || actionPending;
  const blocked = Boolean(activeRevisionBlockedMessage);

  useEffect(() => {
    if (!opened) {
      setActionPending(false);
      return;
    }
    // Reset form when opening confirm step.
    if (step === 'confirm') {
      setMode('approve');
      setBaselineName('');
    }
  }, [opened, step]);

  const runAction = (action: () => void | Promise<void>) => {
    if (busy) return;
    setActionPending(true);
    void Promise.resolve(action())
      .catch(() => {
        // Parent handlers already surface errors.
      })
      .finally(() => {
        setActionPending(false);
      });
  };

  const resolvedName = useMemo(() => {
    const trimmed = baselineName.trim();
    return trimmed || 'Baseline';
  }, [baselineName]);

  const handleConfirm = () => {
    runAction(() =>
      onConfirmCreate({
        mode,
        baselineName: resolvedName,
      }),
    );
  };

  const isSaveGate = step === 'save-gate';
  const title = isSaveGate ? 'Save before setting baseline?' : 'Set project baseline';
  const busyLabel = isSaveGate
    ? 'Saving changes…'
    : mode === 'draft'
      ? 'Saving draft…'
      : 'Starting baseline…';

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      centered
      closeOnClickOutside={!busy}
      closeOnEscape={!busy}
      portalProps={modalContainer ? { target: modalContainer } : undefined}
    >
      <TaskModalBusyOverlay busy={busy} label={busyLabel}>
        <Stack gap="md">
          {isSaveGate ? (
            <Text size="sm">
              Baseline is created from the schedule <strong>saved on the server</strong>. You have
              unsaved changes
              {projectDisplayName ? (
                <>
                  {' '}
                  on <strong>{projectDisplayName}</strong>
                </>
              ) : null}
              . Save them first, then create the baseline.
            </Text>
          ) : blocked ? (
            <Text size="sm" c="orange.8">
              {activeRevisionBlockedMessage}
            </Text>
          ) : (
            <>
              <Text size="sm">
                Create a baseline from the schedule currently saved on the server
                {projectDisplayName ? (
                  <>
                    {' '}
                    for <strong>{projectDisplayName}</strong>
                  </>
                ) : null}
                . Existing baselines are not changed.
              </Text>

              <Radio.Group
                label="Mode"
                value={mode}
                onChange={(value) => setMode(value as SetBaselineCreateMode)}
              >
                <Stack gap="xs" mt={6}>
                  <Radio
                    value="approve"
                    disabled={busy}
                    label="Create & approve"
                    description="Freezes the saved schedule into a baseline slot immediately. Progress appears in the request tracker."
                  />
                  <Radio
                    value="draft"
                    disabled={busy}
                    label="Save as draft for approval"
                    description="Creates a draft revision you can submit for approval later. Does not write approved baselines until approved."
                  />
                </Stack>
              </Radio.Group>

              <TextInput
                label="Baseline name"
                placeholder="e.g. Baseline 0"
                value={baselineName}
                onChange={(e) => setBaselineName(e.currentTarget.value)}
                disabled={busy}
                description={`Will be saved as “${resolvedName}” if left blank or incomplete.`}
              />
            </>
          )}
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose} disabled={busy} type="button">
              {blocked && !isSaveGate ? 'Close' : 'Cancel'}
            </Button>
            {isSaveGate ? (
              <Button
                loading={busy}
                disabled={busy}
                onClick={() => runAction(onSaveAndContinue)}
                {...busyActionButtonProps(busy, 'brand')}
              >
                Save &amp; continue
              </Button>
            ) : blocked ? null : (
              <Button
                loading={busy}
                disabled={busy}
                onClick={handleConfirm}
                {...busyActionButtonProps(busy, 'brand')}
              >
                {mode === 'draft' ? 'Save draft' : 'Create baseline'}
              </Button>
            )}
          </Group>
        </Stack>
      </TaskModalBusyOverlay>
    </Modal>
  );
}
